import { format } from 'date-fns';

import {
  ILoyaltyQuestProgress,
  ILoyaltyQuestUi,
  LoyaltyQuestPrizeType,
  LoyaltyQuestStatus,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';

import { ChallengeHomeCardProps } from '../components';

export function getChallengeButtonText(status: LoyaltyQuestStatus) {
  switch (status) {
    case LoyaltyQuestStatus.NOT_ENROLLED:
      return 'Start Challenge';
    case LoyaltyQuestStatus.COMPLETED:
      return undefined;
    default:
      return 'Order Now';
  }
}

function formatDateAsLocal(dateString: string) {
  try {
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);

    const date = new Date(year, month - 1, day);

    return format(date, 'MMMM do');
  } catch (error) {
    // Fallback
    return format(new Date(), 'MMMM do');
  }
}

export function getChallengeDescription(challenge: ILoyaltyQuestUi) {
  if (challenge.status === LoyaltyQuestStatus.COMPLETED) {
    if (challenge.prize?.type === LoyaltyQuestPrizeType.BONUS_POINTS) {
      return `${challenge.prize?.points} points were applied to your account 🎉`;
    }
    return 'Your prize is ready to be redeemed 🎉';
  }

  return `From ${formatDateAsLocal(challenge.startDate)} to ${formatDateAsLocal(
    challenge.endDate
  )}`;
}

export function getChallengeBadge(
  status: LoyaltyQuestStatus,
  progress?: ILoyaltyQuestProgress | null
) {
  switch (status) {
    case LoyaltyQuestStatus.ACTIVATED:
      return 'Challenge Started';

    case LoyaltyQuestStatus.NOT_COMPLETED:
      return `${progress?.current}/${progress?.total} Completed`;

    case LoyaltyQuestStatus.COMPLETED:
      return 'Challenge Completed';

    default:
      return undefined;
  }
}

export function createChallengeHomeCardProps(
  challenge: ILoyaltyQuestUi,
  isDesktop: boolean,
  onPressChallenge: (challenge: ILoyaltyQuestUi) => Promise<void>,
  showChallengeInfo: (challengeId: string) => void
): ChallengeHomeCardProps {
  const onPress = () => onPressChallenge(challenge);
  const onPressInfo = () => showChallengeInfo(challenge.id);
  if (isDesktop) {
    return createChallengeHomeCardPropsDesktop(challenge, onPress, onPressInfo);
  }
  switch (challenge.status) {
    case LoyaltyQuestStatus.COMPLETED:
      return {
        badge: getChallengeBadge(challenge.status, challenge.progress),
        description: getChallengeDescription(challenge),
        progress: challenge.progress,
        onPressInfo,
      };

    case LoyaltyQuestStatus.NOT_ENROLLED:
      return {
        variant: 'secondary',
        buttonText: getChallengeButtonText(challenge.status) ?? '',
        title: challenge.name ?? '',
        image: challenge.cardImage,
        onPressInfo,
        onPress,
      };

    default:
      return {
        progress: challenge.progress,
        title: challenge.name ?? '',
        buttonText: getChallengeButtonText(challenge.status),
        onPressInfo,
        onPress,
      };
  }
}

function createChallengeHomeCardPropsDesktop(
  challenge: ILoyaltyQuestUi,
  onPress: () => void,
  onPressInfo: () => void
): ChallengeHomeCardProps {
  switch (challenge.status) {
    case LoyaltyQuestStatus.COMPLETED:
      return {
        image: challenge.cardImage,
        title: challenge.name ?? '',
        onPressInfo,
        badge: getChallengeBadge(challenge.status, challenge.progress),
        description: getChallengeDescription(challenge),
      };

    default:
      return {
        image: challenge.cardImage,
        title: challenge.name ?? '',
        progress: challenge.progress,
        onPressInfo,
        onPress,
        buttonText: getChallengeButtonText(challenge.status),
      };
  }
}
